import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Checkbox,
  IconButton,
  Button,
} from '@mui/material';
import {
  MapPin,
  Briefcase,
  Eye,
  Phone,
  UserPlus,
  CheckCircle2,
} from 'lucide-react';
import { NameEmailAvatar } from '../../../ui';
import { CandidateType } from '../constants/mockData';

interface CandidateCardProps {
  candidate: CandidateType;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  getMatchColor: (percentage: number) => string;
  isSelected?: boolean;
  onSelect?: () => void;
  onViewProfile?: (candidate: CandidateType) => void;
  onInvite?: (candidate: CandidateType) => void;
}

export const CandidateCard: React.FC<CandidateCardProps> = ({
  candidate,
  getStatusText,
  getMatchColor,
  isSelected = false,
  onSelect,
  onViewProfile,
  onInvite,
}) => {
  const scorePercent = Math.round(candidate.score * 100);
  const expYears = Math.floor(candidate.experience);
  const latestRole = candidate.work_experience?.[0]?.job_title ?? null;
  const isAvailable =
    candidate.status === 'Open to Opportunities' ||
    candidate.status === 'Actively Looking';

  return (
    <Paper
      elevation={0}
      onClick={() => onViewProfile?.(candidate)}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'grey.200',
        borderRadius: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: isSelected ? '0 0 0 2px rgba(79, 70, 229, 0.2)' : 'none',
        '&:hover': {
          borderColor: isSelected ? 'primary.main' : 'grey.300',
          boxShadow: 2,
        },
      }}
    >
      {/* Top bar: checkbox + score */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 1.5,
        }}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.();
          }}
          sx={{ p: 0 }}
        />

        <Box
          className={getMatchColor(scorePercent)}
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            px: 1,
            py: 0.25,
            borderRadius: 5,
            color: 'common.white',
          }}
        >
          {scorePercent}%
        </Box>
      </Box>

      <Box sx={{ px: 2, pb: 2, pt: 1.5, display: 'flex', flexDirection: 'column', flex: 1, gap: 1.5 }}>
        {/* Avatar + Name + Location + Role */}
        <Box>
          <Box sx={{ flexShrink: 0, mb: 1 }}>
            <NameEmailAvatar
              avatar={candidate.avatar}
              name={candidate.name}
              email={candidate.email}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {latestRole && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {latestRole}
              </Typography>
            )}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {candidate.location && (
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <MapPin size={12} color="#94a3b8" />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.625rem' }}>
                    {candidate.location}
                  </Typography>
                </Stack>
              )}
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <Briefcase size={12} color="#94a3b8" />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.625rem' }}>
                  {expYears}y exp
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto !important' }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: isAvailable ? 'success.main' : 'grey.400',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.5625rem',
                    color: isAvailable ? 'success.main' : 'text.disabled',
                  }}
                >
                  {getStatusText(candidate.status)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* Skills */}
        {candidate.skills.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {candidate.skills.slice(0, 5).map((skill, i) => (
              <Chip
                key={i}
                label={skill}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.625rem',
                  height: 20,
                  bgcolor: 'grey.50',
                  borderColor: 'grey.200',
                  color: 'text.secondary',
                }}
              />
            ))}
            {candidate.skills.length > 5 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.625rem', alignSelf: 'center' }}>
                +{candidate.skills.length - 5} more
              </Typography>
            )}
          </Stack>
        )}

        {/* Invite Lists */}
        {candidate.inviteLists && candidate.inviteLists.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 1,
              bgcolor: 'primary.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.100',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <CheckCircle2 size={12} color="#6366f1" />
              <Typography variant="caption" sx={{ fontSize: '0.5625rem', fontWeight: 700, color: 'primary.main', textTransform: 'uppercase' }}>
                Active in lists
              </Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {candidate.inviteLists.slice(0, 2).map((list, idx) => (
                <Chip
                  key={idx}
                  label={list}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.625rem',
                    bgcolor: 'background.paper',
                    color: 'primary.dark',
                    border: '1px solid',
                    borderColor: 'primary.200',
                  }}
                />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Action Bar */}
        <Box
          sx={{
            pt: 1.5,
            mt: 'auto',
            borderTop: '1px solid',
            borderColor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onViewProfile?.(candidate);
              }}
              title="View Profile"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'primary.50' } }}
            >
              <Eye size={15} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
              }}
              title="Contact"
              sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'grey.100' } }}
            >
              <Phone size={15} />
            </IconButton>
          </Stack>

          <Button
            size="small"
            variant="contained"
            color="inherit"
            startIcon={<UserPlus size={13} />}
            onClick={(e) => {
              e.stopPropagation();
              onInvite?.(candidate);
            }}
            sx={{
              bgcolor: 'grey.900',
              color: 'common.white',
              fontSize: '0.6875rem',
              fontWeight: 700,
              textTransform: 'none',
              px: 1.5,
              py: 0.5,
              '&:hover': { bgcolor: 'primary.main' },
            }}
          >
            Invite
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
